import React, { useContext } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Popconfirm,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import BouncedEmailDetailsContext from '@totem/components/administration/bouncedEmails/details/BouncedEmailDetailsContext';

const { Text } = Typography;

const BouncedEmailAccounts = () => {
  const { details, working, disableUser, disableRemoteAccess, lastSteps } =
    useContext(BouncedEmailDetailsContext);

  const user = details?.user;
  const remoteAccess = details?.remoteAccess;
  const activeOrganizations = (user?.organizations ?? []).filter(
    (org) => org.isActive,
  );
  const disabledOrganizations = (user?.organizations ?? []).filter(
    (org) => !org.isActive,
  );
  const failedSteps = lastSteps.filter((step) => !step.success);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title="Portal User Account"
          extra={
            user && (
              <Popconfirm
                title={`Deactivate ${user.name || user.email} in all ${activeOrganizations.length} organization(s)?`}
                okText="Disable"
                okButtonProps={{ danger: true }}
                onConfirm={disableUser}
                disabled={activeOrganizations.length === 0}
              >
                <Button
                  danger
                  size="small"
                  disabled={working || activeOrganizations.length === 0}
                >
                  Disable User
                </Button>
              </Popconfirm>
            )
          }
        >
          {user ? (
            <>
              <List
                size="small"
                header={user.name || user.email}
                dataSource={activeOrganizations}
                locale={{ emptyText: 'Not active in any organization' }}
                renderItem={(org) => (
                  <List.Item>
                    <Space>
                      {org.name}
                      <Tag color="success">Active</Tag>
                    </Space>
                  </List.Item>
                )}
              />
              {disabledOrganizations.length > 0 && (
                <Tooltip
                  title={disabledOrganizations.map((org) => (
                    <div key={org.id}>{org.name}</div>
                  ))}
                >
                  <Text type="secondary">
                    Disabled in {disabledOrganizations.length} other
                    organization(s)
                  </Text>
                </Tooltip>
              )}
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No portal user with this address"
            />
          )}
        </Card>
      </Col>
      <Col xs={24} lg={12}>
        <Card
          size="small"
          title="Remote Access Account"
          extra={
            remoteAccess && (
              <Popconfirm
                title={`Remove ${remoteAccess.name || remoteAccess.email} from all ${remoteAccess.roles.length} remote access role(s)?`}
                okText="Remove"
                okButtonProps={{ danger: true }}
                onConfirm={disableRemoteAccess}
                disabled={remoteAccess.roles.length === 0}
              >
                <Button
                  danger
                  size="small"
                  disabled={working || remoteAccess.roles.length === 0}
                >
                  Disable Remote Access
                </Button>
              </Popconfirm>
            )
          }
        >
          {remoteAccess ? (
            <List
              size="small"
              header={remoteAccess.name || remoteAccess.email}
              dataSource={remoteAccess.roles}
              locale={{ emptyText: 'Not assigned to any roles' }}
              renderItem={(role) => (
                <List.Item>
                  <Space>
                    {role.name}
                    {role.hierarchyReference?.organization?.name && (
                      <Tag>{role.hierarchyReference.organization.name}</Tag>
                    )}
                    {role.hierarchyReference?.building?.name && (
                      <Tag>{role.hierarchyReference.building.name}</Tag>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No remote access account with this address"
            />
          )}
          {failedSteps.length > 0 && (
            <Alert
              type="warning"
              style={{ marginTop: 8 }}
              message="Some remote access steps failed"
              description={failedSteps.map((step) => (
                <div key={`${step.stepName}:${step.outcome}`}>
                  {step.outcome}
                  {step.error ? ` (${step.error})` : ''}
                </div>
              ))}
            />
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default BouncedEmailAccounts;
